import { Controller } from "stimulus";
import { layoutBars, layoutBlocks, groupHeight, blockRowHeight, matchesQuery, placeAtPointer } from "../boarding_calendar/layout";
import { rememberedView, withRemembered, zoomEntries } from "../boarding_calendar/history";
import { noMatchesMessage } from "../boarding_calendar/movements";
import { buildBar, buildOutsideLabel, buildBlockBar, buildHoverCard, buildBlockPopover, buildSheet, buildBlockSheet, buildDrillDown } from "../boarding_calendar/dom";

const MOBILE_QUERY = "(max-width: 639.98px)";
const STORAGE_PREFIX = "bp.boardingCalendar.";
const RANGES = ["weekly", "monthly"];
const VIEWS = ["calendar", "occupancy"];
const CARD_DELAY = 150;

let activeCalendar = null;

function interceptTurbolinksPops() {
  const turbolinks = window.Turbolinks && window.Turbolinks.controller;
  if (!turbolinks || turbolinks.boardingCalendarIntercept) return;
  if (typeof turbolinks.historyPoppedToLocationWithRestorationIdentifier !== "function") return;

  const original = turbolinks.historyPoppedToLocationWithRestorationIdentifier;
  turbolinks.historyPoppedToLocationWithRestorationIdentifier = function (...args) {
    if (activeCalendar && activeCalendar.restoreFromPop(window.history.state)) return undefined;
    return original.apply(this, args);
  };
  turbolinks.boardingCalendarIntercept = true;
}

export default class extends Controller {
  static values = {
    url: String,
    date: String,
    range: String,
    view: String,
    locationId: String,
  };

  static targets = ["content"];

  connect() {
    this.collapsed = new Set();
    this.payload = null;
    this.byId = {};
    this.blocksById = {};
    this.query = "";
    this.selected = null;
    this.selectedCell = null;
    this.card = null;
    this.cardTimer = null;
    this.loadToken = 0;
    this.lastWidth = 0;
    this.mediaQuery = window.matchMedia(MOBILE_QUERY);
    this.pageKey = this.currentPageKey();

    this.restorePreferences();

    this.boundSearch = (event) => {
      this.query = (event.detail && event.detail.query) || "";
      this.layout();
    };
    this.boundDocumentClick = this.handleDocumentClick.bind(this);
    this.boundKeydown = this.handleKeydown.bind(this);
    this.boundMediaChange = () => this.handleBreakpointChange();

    document.addEventListener("boarding-search:changed", this.boundSearch);
    document.addEventListener("click", this.boundDocumentClick);
    document.addEventListener("keydown", this.boundKeydown);
    if (this.mediaQuery.addEventListener) this.mediaQuery.addEventListener("change", this.boundMediaChange);

    this.boundTabShown = () => this.handleTabShown();
    this.tabLink = document.querySelector('a[href="#calendar"]');
    if (this.tabLink && window.$) window.$(this.tabLink).on("shown.bs.tab", this.boundTabShown);

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
      this.resizeObserver.observe(this.contentTarget);
    }

    activeCalendar = this;
    interceptTurbolinksPops();

    this.load();
  }

  disconnect() {
    if (activeCalendar === this) activeCalendar = null;

    document.removeEventListener("boarding-search:changed", this.boundSearch);
    document.removeEventListener("click", this.boundDocumentClick);
    document.removeEventListener("keydown", this.boundKeydown);
    if (this.mediaQuery.removeEventListener) this.mediaQuery.removeEventListener("change", this.boundMediaChange);
    if (this.tabLink && window.$) window.$(this.tabLink).off("shown.bs.tab", this.boundTabShown);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.layoutTimer) clearTimeout(this.layoutTimer);
    if (this.flatpickrInstance) this.flatpickrInstance.destroy();
    this.hideCard();
  }

  get mobile() {
    return this.mediaQuery.matches;
  }

  restorePreferences() {
    const remembered = rememberedView(window.history.state);
    if (remembered) {
      this.applyRemembered(remembered);
      return;
    }

    const stored = (key) => {
      try {
        return window.localStorage.getItem(STORAGE_PREFIX + key);
      } catch (error) {
        return null;
      }
    };

    const range = stored("range");
    if (RANGES.includes(range)) {
      this.rangeValue = range;
    } else if (this.mobile) {
      this.rangeValue = "weekly";
    } else if (!RANGES.includes(this.rangeValue)) {
      this.rangeValue = "weekly";
    }

    const view = stored("view");
    this.viewValue = VIEWS.includes(view) ? view : "calendar";
  }

  applyRemembered(remembered) {
    this.rangeValue = remembered.range;
    this.dateValue = remembered.date;
    this.viewValue = remembered.view;
    this.persist("range", remembered.range);
    this.persist("view", remembered.view);
  }

  persist(key, value) {
    try {
      window.localStorage.setItem(STORAGE_PREFIX + key, value);
    } catch (error) {
      // storage can be unavailable in private windows
    }
  }

  load() {
    this.syncHistory();
    this.closeSelection(false);
    this.hideCard();
    this.clearCell();
    const token = ++this.loadToken;
    const params = new URLSearchParams({
      date: this.dateValue,
      type: this.rangeValue,
      view: this.viewValue,
      location_id: this.locationIdValue,
    });

    this.contentTarget.classList.add("bc-loading");
    fetch(`${this.urlValue}?${params.toString()}`, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        if (token !== this.loadToken) return;
        this.contentTarget.innerHTML = html;
        this.contentTarget.classList.remove("bc-loading");
        this.afterRender();
      })
      .catch(() => {
        if (token !== this.loadToken) return;
        this.contentTarget.classList.remove("bc-loading");
        this.contentTarget.innerHTML = '<div class="bc-error">The boarding calendar could not be loaded. Please refresh the page and try again.</div>';
      });
  }

  afterRender() {
    const script = this.contentTarget.querySelector("script[data-boarding-calendar-payload]");
    this.payload = script ? JSON.parse(script.textContent) : null;
    this.byId = {};
    this.blocksById = {};
    if (this.payload) {
      this.payload.bookings.forEach((booking) => (this.byId[booking.id] = booking));
      this.payload.blocks.forEach((block) => (this.blocksById[block.id] = block));
    }

    this.contentTarget.querySelectorAll(".bc-group").forEach((group) => {
      const collapsed = this.collapsed.has(group.dataset.groupKey);
      group.classList.toggle("bc-collapsed", collapsed);
      const label = group.querySelector(".bc-group-label");
      if (label) label.setAttribute("aria-expanded", collapsed ? "false" : "true");
    });

    this.lastWidth = this.contentTarget.clientWidth;
    this.layout();
  }

  localDateString(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  parseDate(value) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  previous() {
    this.shift(-1);
  }

  next() {
    this.shift(1);
  }

  shift(direction) {
    const date = this.parseDate(this.dateValue);
    if (this.rangeValue === "weekly") {
      date.setDate(date.getDate() + 7 * direction);
    } else {
      date.setDate(1);
      date.setMonth(date.getMonth() + direction);
    }
    this.dateValue = this.localDateString(date);
    this.load();
  }

  previousFromBar(event) {
    event.stopPropagation();
    event.preventDefault();
    this.shift(-1);
  }

  nextFromBar(event) {
    event.stopPropagation();
    event.preventDefault();
    this.shift(1);
  }

  today(event) {
    this.dateValue = event.currentTarget.dataset.date;
    this.load();
  }

  pickDate(event) {
    const label = event.currentTarget;
    if (label._flatpickr) {
      label._flatpickr.open();
      return;
    }

    const controller = this;
    this.flatpickrInstance = flatpickr(label, {
      disableMobile: "true",
      defaultDate: this.dateValue,
      onChange(selectedDates) {
        if (selectedDates.length === 1) {
          controller.dateValue = controller.localDateString(selectedDates[0]);
          controller.load();
        }
      },
    });
    this.flatpickrInstance.open();
  }

  setView(event) {
    const view = event.currentTarget.dataset.view;
    if (view === this.viewValue) return;
    this.viewValue = view;
    this.persist("view", view);
    this.load();
  }

  setRange(event) {
    const range = event.currentTarget.dataset.range;
    if (range === this.rangeValue) return;
    this.rangeValue = range;
    this.persist("range", range);
    this.load();
  }

  showWeek(event) {
    const date = event.currentTarget.dataset.date;
    const entries = zoomEntries(window.history.state, this.currentView(), { range: "weekly", date, view: this.viewValue });

    window.history.replaceState(entries.current, "");
    window.history.pushState(entries.next, "");

    this.dateValue = date;
    this.rangeValue = "weekly";
    this.persist("range", "weekly");
    this.load();
  }

  currentPageKey() {
    return `${window.location.pathname}${window.location.search}`;
  }

  currentView() {
    return { range: this.rangeValue, date: this.dateValue, view: this.viewValue };
  }

  syncHistory() {
    const state = window.history.state;
    if (!rememberedView(state)) return;

    window.history.replaceState(withRemembered(state, this.currentView()), "");
  }

  restoreFromPop(state) {
    const remembered = rememberedView(state);
    if (!remembered || this.currentPageKey() !== this.pageKey) return false;

    this.applyRemembered(remembered);
    this.load();
    return true;
  }

  toggleGroup(event) {
    const group = event.currentTarget.closest(".bc-group");
    const key = group.dataset.groupKey;
    const label = group.querySelector(".bc-group-label");
    const collapsed = !this.collapsed.has(key);

    if (collapsed) {
      this.collapsed.add(key);
    } else {
      this.collapsed.delete(key);
    }

    group.classList.toggle("bc-collapsed", collapsed);
    label.setAttribute("aria-expanded", collapsed ? "false" : "true");
    if (!collapsed && event.detail === 0) label.focus();

    this.layoutGroup(group.querySelector(".bc-group-body"));
    this.validateSelection();
  }

  scheduleLayout() {
    if (this.layoutTimer) clearTimeout(this.layoutTimer);
    this.layoutTimer = setTimeout(() => {
      const width = this.contentTarget.clientWidth;
      if (Math.abs(width - this.lastWidth) < 1) return;
      this.lastWidth = width;
      this.closeSelection(false);
      this.layout();
      this.renderCell();
    }, 60);
  }

  handleTabShown() {
    this.lastWidth = this.contentTarget.clientWidth;
    this.layout();
    this.renderCell();
  }

  handleBreakpointChange() {
    this.closeSelection(false);
    this.layout();
    this.renderCell();
  }

  syncViewWidth() {
    const scroller = this.contentTarget.querySelector(".bc-scroll");
    if (!scroller) return;

    const style = window.getComputedStyle(scroller);
    const width = scroller.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    if (width > 0) scroller.style.setProperty("--bc-view-w", `${width}px`);
  }

  syncStickyHeader() {
    const scroller = this.contentTarget.querySelector(".bc-scroll");
    if (!scroller || !scroller.firstElementChild) return;

    const style = window.getComputedStyle(scroller);
    const inner = scroller.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    scroller.classList.toggle("bc-fits", scroller.firstElementChild.getBoundingClientRect().width <= inner + 0.5);

    const bar = document.querySelector(".sticky-top-bar");
    const stuck = bar && window.getComputedStyle(bar).position === "sticky";
    this.element.style.setProperty("--bc-sticky-top", `${stuck ? Math.floor(bar.getBoundingClientRect().height) : 0}px`);
  }

  layout() {
    this.syncStickyHeader();
    if (!this.payload) return;
    this.syncViewWidth();
    this.hideCard();
    this.filterRows();
    this.layoutBlockedRow();
    this.contentTarget.querySelectorAll(".bc-group-body").forEach((body) => this.layoutGroup(body));
    this.validateSelection();
  }

  filterRows() {
    const timeline = this.contentTarget.querySelector(".bc-timeline");
    if (!timeline) return;

    const searching = this.query.trim() !== "";
    const groups = timeline.querySelectorAll(".bc-group");
    let shown = 0;
    groups.forEach((group) => {
      const key = group.dataset.groupKey;
      const match = !searching || this.payload.bookings.some((booking) => booking.g === key && matchesQuery(booking, this.query));
      group.classList.toggle("bc-search-miss", !match);
      if (match) shown += 1;
    });

    const blocked = timeline.querySelector(".bc-blocked-row");
    if (blocked) blocked.classList.toggle("bc-search-miss", searching);

    let empty = timeline.querySelector(".bc-search-empty");
    if (searching && groups.length > 0 && shown === 0) {
      if (!empty) {
        empty = document.createElement("div");
        empty.className = "bc-empty bc-search-empty";
        groups[groups.length - 1].after(empty);
      }
      empty.textContent = noMatchesMessage(this.query);
    } else if (empty) {
      empty.remove();
    }
  }

  layoutBlockedRow() {
    const body = this.contentTarget.querySelector("[data-blocked-body]");
    if (!body) return;

    body.querySelectorAll(".bc-block-bar").forEach((node) => node.remove());
    const width = body.clientWidth;
    if (width === 0) return;

    const blocks = this.payload.blocks.filter((block) => block.all);
    const { bars, laneCount } = layoutBlocks({ blocks, start: 0, end: this.payload.days, dayPx: width / this.payload.days });

    body.style.minHeight = `${blockRowHeight(laneCount)}px`;
    this.appendBlockBars(body, bars);
  }

  appendBlockBars(body, bars) {
    bars.forEach((bar) => {
      const selected = this.selected && this.selected.kind === "block" && this.selected.id === bar.id;
      body.appendChild(buildBlockBar(bar, this.blocksById[bar.id], selected));
    });
  }

  layoutGroup(body) {
    if (!body || !this.payload) return;

    body.querySelectorAll(".bc-bar, .bc-outside, .bc-block-bar").forEach((node) => node.remove());
    const group = body.closest(".bc-group");

    if (group.classList.contains("bc-collapsed")) {
      body.style.minHeight = "";
      return;
    }

    const width = body.clientWidth;
    if (width === 0) return;

    const key = body.dataset.groupBody;
    const dayPx = width / this.payload.days;
    const blocks = this.payload.blocks.filter((block) => !block.all && block.keys.includes(key));
    const blockLayout = layoutBlocks({ blocks, start: 0, end: this.payload.days, dayPx });
    const offset = blockLayout.laneCount > 0 ? blockRowHeight(blockLayout.laneCount) : 0;

    const bookings = this.payload.bookings.filter((booking) => booking.g === key && matchesQuery(booking, this.query));
    const { bars, laneCount } = layoutBars({ bookings, start: 0, end: this.payload.days, dayPx, mobile: this.mobile, offset });

    body.style.minHeight = `${(bars.length > 0 ? groupHeight(laneCount) : 0) + offset || 42}px`;
    this.appendBlockBars(body, blockLayout.bars);

    const unit = this.rangeValue === "weekly" ? "week" : "month";
    bars.forEach((bar) => {
      const booking = this.byId[bar.id];
      const selected = this.selected && this.selected.kind === "booking" && this.selected.id === bar.id;
      body.appendChild(buildBar(bar, booking, { selected, unit, mobile: this.mobile }));
      if (bar.outside) body.appendChild(buildOutsideLabel(bar, booking, { selected, mobile: this.mobile }));
    });
  }

  isSelected(kind, id) {
    return Boolean(this.selected) && this.selected.kind === kind && this.selected.id === id;
  }

  selectBar(event) {
    this.hideCard();
    if (!this.mobile) return;

    event.preventDefault();
    event.stopPropagation();
    const anchor = event.currentTarget;
    const id = Number(anchor.dataset.bookingId);

    if (this.isSelected("booking", id)) {
      this.closeSelection();
      return;
    }

    this.closeSelection(false);
    this.openBooking(id, this.contentTarget.querySelector(`.bc-bar[data-booking-id="${id}"]`) || anchor);
  }

  openBooking(id, anchor) {
    const booking = this.byId[id];
    if (!booking) return;

    this.selected = { kind: "booking", id };
    this.anchor = anchor;
    this.contentTarget.querySelectorAll(`[data-booking-id="${id}"]`).forEach((node) => {
      node.classList.add(node.classList.contains("bc-outside") ? "bc-outside-selected" : "bc-bar-selected");
      node.setAttribute("aria-expanded", "true");
    });

    this.overlay = buildSheet(booking, { onClose: () => this.closeSelection() });
    this.contentTarget.appendChild(this.overlay);
    this.focusOverlay();
  }

  selectBlock(event) {
    event.stopPropagation();
    this.hideCard();
    const anchor = event.currentTarget;
    const id = Number(anchor.dataset.blockId);

    if (this.isSelected("block", id)) {
      this.closeSelection();
      return;
    }

    this.closeSelection(false);
    this.openBlock(id, anchor, event.detail > 0 ? { x: event.clientX, y: event.clientY } : null);
  }

  openBlock(id, anchor, point = null) {
    const block = this.blocksById[id];
    if (!block) return;

    this.selected = { kind: "block", id };
    this.anchor = anchor;
    this.contentTarget.querySelectorAll(`[data-block-id="${id}"]`).forEach((node) => {
      node.classList.add("bc-block-bar-selected");
      node.setAttribute("aria-expanded", "true");
    });

    if (this.mobile) {
      this.overlay = buildBlockSheet(block, { onClose: () => this.closeSelection() });
      this.contentTarget.appendChild(this.overlay);
    } else {
      this.overlay = buildBlockPopover(block, { onClose: () => this.closeSelection() });
      this.contentTarget.appendChild(this.overlay);
      if (point) {
        this.placeNear(this.overlay, point);
      } else {
        this.positionPopover(this.overlay, anchor);
      }
    }
    this.focusOverlay();
  }

  focusOverlay() {
    const close = this.overlay.querySelector(".bc-pop-close, .bc-sheet-close");
    if (close) close.focus({ preventScroll: true });
  }

  positionPopover(popover, anchor) {
    const container = this.contentTarget.getBoundingClientRect();
    const target = anchor.getBoundingClientRect();
    const width = popover.offsetWidth;
    const height = popover.offsetHeight;

    let left = target.left - container.left;
    left = Math.max(0, Math.min(left, container.width - width));

    let top = target.bottom - container.top + 6;
    if (target.bottom + height + 12 > window.innerHeight && target.top - height - 12 > 0) {
      top = target.top - container.top - height - 6;
    }

    popover.style.left = `${left}px`;
    popover.style.top = `${top}px`;
  }

  placeNear(popover, point) {
    const container = this.contentTarget.getBoundingClientRect();
    const spot = placeAtPointer({
      x: point.x,
      y: point.y,
      width: popover.offsetWidth,
      height: popover.offsetHeight,
      container: { left: container.left, top: container.top, right: container.right },
      viewportHeight: window.innerHeight,
    });

    popover.style.left = `${spot.left}px`;
    popover.style.top = `${spot.top}px`;
  }

  closeSelection(restoreFocus = true) {
    if (!this.selected && !this.overlay) return;

    const anchor = this.anchor;
    if (this.overlay) this.overlay.remove();
    this.contentTarget.querySelectorAll(".bc-bar-selected, .bc-outside-selected, .bc-block-bar-selected").forEach((node) => {
      node.classList.remove("bc-bar-selected", "bc-outside-selected", "bc-block-bar-selected");
      node.setAttribute("aria-expanded", "false");
    });

    this.overlay = null;
    this.selected = null;
    this.anchor = null;
    if (restoreFocus && anchor && anchor.isConnected) anchor.focus({ preventScroll: true });
  }

  validateSelection() {
    if (!this.selected) return;

    const selector = this.selected.kind === "booking" ? `.bc-bar[data-booking-id="${this.selected.id}"]` : `.bc-block-bar[data-block-id="${this.selected.id}"]`;
    if (!this.contentTarget.querySelector(selector)) this.closeSelection(false);
  }

  showCard(event) {
    if (this.mobile) return;

    const anchor = event.currentTarget;
    this.pointer = event.type === "mouseenter" ? { x: event.clientX, y: event.clientY } : null;
    clearTimeout(this.cardTimer);
    this.cardTimer = setTimeout(() => this.openCard(anchor), event.type === "mouseenter" ? CARD_DELAY : 0);
  }

  movePointer(event) {
    if (this.mobile || !this.pointer) return;

    this.pointer = { x: event.clientX, y: event.clientY };
    if (this.card) this.placeNear(this.card, this.pointer);
  }

  hideCard() {
    clearTimeout(this.cardTimer);
    this.pointer = null;
    if (!this.card) return;

    this.card.remove();
    this.card = null;
  }

  openCard(anchor) {
    const booking = this.byId[Number(anchor.dataset.bookingId)];
    if (!booking || !anchor.isConnected) return;

    if (this.card) this.card.remove();
    this.card = buildHoverCard(booking);
    this.contentTarget.appendChild(this.card);
    if (this.pointer) {
      this.placeNear(this.card, this.pointer);
    } else {
      this.positionPopover(this.card, this.contentTarget.querySelector(`.bc-bar[data-booking-id="${booking.id}"]`) || anchor);
    }
  }

  selectCell(event) {
    event.stopPropagation();
    const cell = event.currentTarget;
    const key = cell.dataset.groupKey;
    const day = Number(cell.dataset.dayIndex);

    if (this.selectedCell && this.selectedCell.key === key && this.selectedCell.day === day) {
      this.clearCell();
      return;
    }

    this.selectedCell = { key, day };
    this.renderCell();
  }

  clearCell() {
    this.selectedCell = null;
    this.renderCell();
  }

  renderCell() {
    if (!this.payload) return;

    this.contentTarget.querySelectorAll(".bc-cell-selected").forEach((node) => node.classList.remove("bc-cell-selected"));
    this.contentTarget.querySelectorAll(".bc-drill").forEach((node) => {
      node.hidden = true;
      node.replaceChildren();
    });

    if (!this.selectedCell) return;

    const { key, day } = this.selectedCell;
    const cell = this.contentTarget.querySelector(`.bc-cell[data-group-key="${key}"][data-day-index="${day}"]`);
    if (!cell) {
      this.selectedCell = null;
      return;
    }

    cell.classList.add("bc-cell-selected");
    const container = this.mobile ? this.contentTarget.querySelector(`[data-drill-mobile="${key}"]`) : this.contentTarget.querySelector("[data-drill-desktop]");
    if (!container) return;

    const group = this.payload.groups.find((item) => item.key === key);
    container.appendChild(
      buildDrillDown({
        groupName: group ? group.name : "",
        dayLabel: this.payload.day_labels[day],
        bookings: this.payload.bookings.filter((booking) => booking.g === key),
        dayIndex: day,
        compact: this.mobile,
        onClose: () => this.clearCell(),
      })
    );
    container.hidden = false;
  }

  handleDocumentClick(event) {
    if (!this.selected) return;
    if (event.target.closest(".bc-popover, .bc-sheet, .bc-bar, .bc-outside, .bc-block-bar")) return;
    this.closeSelection(false);
  }

  handleKeydown(event) {
    if (event.key !== "Escape") return;
    this.hideCard();
    if (this.selected) {
      this.closeSelection();
    } else if (this.selectedCell) {
      this.clearCell();
    }
  }
}
